import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Search, ChevronDown } from "lucide-react";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import { Fundraiser } from "../../../models/FundraisersGridModels";
import { CreateFundraiserModel, FundRaiserGridModel } from "../../../models";

const FundraiserNameContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ClickableFundraiserName = styled.h2`
  font-size: 26px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FundraiserNameText = styled.span`
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #3b82f6;
  }
`;

const DropdownIcon = styled(ChevronDown)<{ isOpen: boolean }>`
  width: 20px;
  height: 20px;
  color: #64748b;
  transition: all 0.2s ease;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  cursor: pointer;

  &:hover {
    color: #3b82f6;
  }
`;

const FundraiserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 400px;
  overflow: hidden;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  margin-top: 8px;
  min-width: 560px;
`;

const DropdownHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  position: relative;
`;

const DropdownSearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: #f8fafc;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const DropdownSearchIcon = styled(Search)`
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: #64748b;
`;

const DropdownList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const DropdownItem = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.2fr 40px;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: all 0.2s ease;
  align-items: center;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const DropdownFundraiserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DropdownFundraiserThumb = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
`;

const DropdownFundraiserDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const DropdownFundraiserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownFundraiserMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DropdownStatusBadge = styled.div<{ status: string }>`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;

  ${(props) => {
    switch (props.status) {
      case "active":
        return "background: #dcfce7; color: #166534;";
      case "completed":
        return "background: #dbeafe; color: #1e40af;";
      case "inactive":
        return "background: #fef3c7; color: #92400e;";
      case "draft":
        return "background: #f3f4f6; color: #374151;";
      default:
        return "background: #f3f4f6; color: #374151;";
    }
  }}
`;

const DropdownProgressColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DropdownProgressAmounts = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
`;

const DropdownProgressBarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DropdownProgressBar = styled.div`
  width: 80px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
`;

const DropdownProgressFill = styled.div<{ percentage: number }>`
  width: ${(props) => Math.min(props.percentage, 100)}%;
  height: 100%;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 2px;
  transition: width 1s ease-out;
`;

const DropdownProgressPercentage = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #1e293b;
  min-width: 35px;
`;

const SelectedCheckIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #10b981;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
`;

interface FundraiserSelectDropdownProps {
  fundraiser: CreateFundraiserModel;
  isOpen: boolean;
  searchTerm: string;
  onToggle: () => void;
  onSearchChange: (value: string) => void;
  onSelect: (selectedFundraiserId: string) => void;
}

export const FundraiserSelectDropdown: React.FC<FundraiserSelectDropdownProps> =
  observer(
    ({
      fundraiser,
      isOpen,
      searchTerm,
      onToggle,
      onSearchChange,
      onSelect,
    }) => {
      const dropdownRef = useRef<HTMLDivElement>(null);
      const searchInputRef = useRef<HTMLInputElement>(null);

      // Check if there's only one fundraiser
      const totalFundraisers = fundraiserStore.fundraisers.length;
      const shouldShowDropdown = totalFundraisers > 1;

      // Handle clicking outside dropdown to close it
      useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target as Node)
          ) {
            if (isOpen && shouldShowDropdown) {
              onToggle();
              onSearchChange("");
            }
          }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }, [isOpen, onToggle, onSearchChange, shouldShowDropdown]);

      // Focus search input when dropdown opens
      useEffect(() => {
        if (isOpen && searchInputRef.current && shouldShowDropdown) {
          setTimeout(() => {
            searchInputRef.current?.focus();
          }, 100);
        }
      }, [isOpen, shouldShowDropdown]);

      // Get all fundraisers sorted by start date (newest to oldest) including current fundraiser
      const getAllFundraisersSorted = () => {
        return fundraiserStore.fundraisers
          .slice()
          .sort(
            (a, b) =>
              new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
          );
      };

      const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(amount);
      };

      // Get all fundraisers for dropdown (excluding current one)
      const getAllFundraisers = () => {
        return getAllFundraisersSorted();
      };

      // Filter fundraisers based on search term
      const getFilteredFundraisers = () => {
        const allFundraisers = getAllFundraisers();
        if (!searchTerm) return allFundraisers;

        const searchLower = searchTerm.toLowerCase();
        return allFundraisers.filter((f) =>
          f.name.toLowerCase().includes(searchLower)
        );
      };

      // Handle fundraiser selection from dropdown
      const handleFundraiserSelect = (
        selectedFundraiserId: string
      ) => {
        onSelect(selectedFundraiserId);
        onSearchChange("");
      };

      // If only one fundraiser exists, show just the name without dropdown functionality
      if (!shouldShowDropdown) {
        return (
          <FundraiserNameContainer>
            <ClickableFundraiserName>
              <FundraiserNameText style={{ cursor: "default" }}>
                {fundraiser.name}
              </FundraiserNameText>
            </ClickableFundraiserName>
          </FundraiserNameContainer>
        );
      }
      return (
        <FundraiserNameContainer ref={dropdownRef}>
          <ClickableFundraiserName onClick={onToggle}>
            <FundraiserNameText onClick={onToggle}>
              {fundraiser.name}
            </FundraiserNameText>
            <DropdownIcon size={16} isOpen={isOpen} onClick={onToggle} />
          </ClickableFundraiserName>

          <FundraiserDropdown isOpen={isOpen}>
            <DropdownHeader>
              <DropdownSearchIcon />
              <DropdownSearchInput
                ref={searchInputRef}
                type="text"
                placeholder="Search fundraisers..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </DropdownHeader>

            <DropdownList>
              {getFilteredFundraisers().map((f) => {
                const progress =
                  f.goal > 0 ? (f.totalRaisedAmount / f.goal) * 100 : 0;
                const barProgress = Math.min(progress, 100);
                const isSelected = f.id === fundraiser.id;

                return (
                  <DropdownItem
                    key={f.id}
                    onClick={() => handleFundraiserSelect(f.id)}
                  >
                    <DropdownFundraiserInfo>
                      {f.mediaItems?.[0]?.fullURL && (
                        <DropdownFundraiserThumb
                          src={f.mediaItems?.[0]?.fullURL}
                         // alt={f.name}
                          // onError={(e) => {
                          //   const target = e.target as HTMLImageElement;
                          //   target.src =
                          //     "https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?w=400&h=300&fit=crop";
                          // }}
                        />
                      )}
                      <DropdownFundraiserDetails>
                        <DropdownFundraiserName>
                          {f.name}
                        </DropdownFundraiserName>
                        <DropdownFundraiserMeta>
                          <DropdownStatusBadge status={f.status}>
                            {f.status}
                          </DropdownStatusBadge>
                        </DropdownFundraiserMeta>
                      </DropdownFundraiserDetails>
                    </DropdownFundraiserInfo>

                    <DropdownProgressColumn>
                      <DropdownProgressAmounts>
                        {formatCurrency(f.totalRaisedAmount)} /{" "}
                        {formatCurrency(f.goal)}
                      </DropdownProgressAmounts>
                      <DropdownProgressBarContainer>
                        <DropdownProgressBar>
                          <DropdownProgressFill percentage={barProgress} />
                        </DropdownProgressBar>
                        <DropdownProgressPercentage>
                          {progress.toFixed(1)}%
                        </DropdownProgressPercentage>
                      </DropdownProgressBarContainer>
                    </DropdownProgressColumn>

                    {isSelected && <SelectedCheckIcon>✓</SelectedCheckIcon>}
                  </DropdownItem>
                );
              })}

              {getFilteredFundraisers().length === 0 && (
                <div
                  style={{
                    padding: "24px",
                    textAlign: "center",
                    color: "#64748b",
                    fontSize: "14px",
                  }}
                >
                  {searchTerm
                    ? `No fundraisers found matching "${searchTerm}"`
                    : "No fundraisers available"}
                </div>
              )}
            </DropdownList>
          </FundraiserDropdown>
        </FundraiserNameContainer>
      );
    }
  );
